import { AxiosPromise } from 'axios';

import { axios } from 'src/app/utils';
import { urlsConfig } from 'src/configs';
import { queryTypes, serviceKanbanTypes, serviceProjectInputTypes } from 'src/types';
import { fileHelper, urlHelper } from '../../helpers';
import { serviceLocals } from 'src/locals';

export const lookupKanbanTable = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceKanbanTypes.KanbanLookupDataType): AxiosPromise<serviceKanbanTypes.KanbanTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    return axios.get(urlHelper.addQueryParameters({ search: value }, urlsConfig.kanban.lookupKanban), {
      blockSpinner,
    });
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.kanban.lookupKanban), {
    blockSpinner,
  });
};

export const lookupKanbanWithEvent = ({
  limit,
  offset,
  ids,
  value,
  order,
  eventIds,
  sortOrder,
  blockSpinner,
  load,
}: serviceKanbanTypes.KanbanLookupDataType): AxiosPromise<serviceKanbanTypes.KanbanTableLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    query.search = value;
  }

  if (eventIds) {
    return axios.get(`${urlsConfig.kanban.lookupKanban}?eventIds=${eventIds}`, {
      blockErrorHandler: true,
    });
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.kanban.lookupKanban), {
    blockSpinner,
  });
};

export const lookupKanbanOfOntology = ({
  limit,
  offset,
  ids,
  value,
  order,
  sortOrder,
  blockSpinner,
  load,
}: serviceKanbanTypes.KanbanLookupDataType): AxiosPromise<serviceKanbanTypes.KanbanOfOntologyLookupResultType> => {
  const query: queryTypes.LookupQueryParametersType = {};

  if (ids) {
    query.ids = ids.join(',');
  }

  if (value) {
    return axios.get(urlHelper.addQueryParameters({ search: value }, urlsConfig.kanban.lookupKanbanOfOntology), {
      blockSpinner,
    });
  }

  if (order && sortOrder) {
    query.order = order;
    query.sortOrder = sortOrder;
  }

  if (limit && offset) {
    query.limit = limit;
    query.offset = offset;
  }

  return axios.get(urlHelper.addQueryParameters(query, urlsConfig.kanban.lookupKanbanOfOntology), {
    blockSpinner,
  });
};

export const createKanban = (
  form: serviceKanbanTypes.KanbanCreateDataType,
  // formData: FormData,
  options: { blockErrorHandler: boolean },
): AxiosPromise<serviceKanbanTypes.KanbanCreateResultType> => {
  const actors = fileHelper.convertArrayData(form.actors);
  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.taskNumberId) {
    formData.append('taskNumberId', form.taskNumberId);
  }

  if (form.kanbanNumber) {
    formData.append('kanbanNumber', form.kanbanNumber);
  }

  if (form.kanbanName) {
    formData.append('kanbanName', form.kanbanName);
  }

  if (form.scriptedById) {
    formData.append('scriptedById', form.scriptedById);
  }

  if (form.scriptedDateTime) {
    formData.append('scriptedDateTime', form.scriptedDateTime.toString());
  }

  if (form.modifiedById) {
    formData.append('modifiedById', form.modifiedById);
  }

  if (form.modifiedDateTime) {
    formData.append('modifiedDateTime', form.modifiedDateTime.toString());
  }
  if (form.emir.length > 0) {
    formData.append('emir', form.emir.join(','));
  }
  if (form.kanbanCoordinatorId) {
    formData.append('kanbanCoordinatorId', form.kanbanCoordinatorId);
  }

  if (form.actors) {
    form.actors.forEach((e) => formData.append('actors', e as any as string));
  }

  if (form.dateTimeGroup) {
    formData.append('dateTimeGroup', form.dateTimeGroup.toString());
  }

  if (form.timeDetails) {
    formData.append('timeDetails', JSON.stringify(form.timeDetails));
  }

  if (form.type) {
    formData.append('type', form.type.toString());
  }

  if (form.projectInputReferenceId) {
    formData.append('projectInputReferenceId', form.projectInputReferenceId);
  }
  if (form.kanbanReferenceId) {
    formData.append('kanbanReferenceId', form.kanbanReferenceId);
  }
  if (form.duration) {
    formData.append('duration', form.duration);
  }

  if (form.description) {
    formData.append('description', form.description);
  }

  if (form.coordinationRemarks) {
    formData.append('coordinationRemarks', form.coordinationRemarks);
  } else {
    formData.append('coordinationRemarks', '');
  }

  // if (form.unitInSimulation) {
  //   formData.append('unitInSimulation', form.unitInSimulation);
  // }
  if (form.units.length > 0) {
    formData.append('units', form.units.join(','));
  }

  if (form.log) {
    formData.append('log', form.log);
  }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  if (form.kanbanStatusId) {
    formData.append('kanbanStatusId', form.kanbanStatusId);
  }
  formData.append('isAnchor', form.isAnchor.toString());

  return axios.post(urlsConfig.kanban.createKanban, formData, { headers: serviceLocals.formDataHeader });
};

export const kanbanEdit = (
  form: serviceKanbanTypes.KanbanEditDataType,
  // formData: FormData,
): AxiosPromise<serviceKanbanTypes.KanbanEditResultType> => {
  const actorsConvert = fileHelper.convertArrayData(form.actors);
  const formData = fileHelper.convertDataURLsToFormData(form.attachments);

  if (form.id !== undefined) {
    formData.append('id', form.id);
  }

  if (form.deletedAttachments !== undefined) {
    form.deletedAttachments.forEach((eachAttachment: any) => {
      formData.append('deletedAttachments', eachAttachment);
    });
  }

  if (form.eventId) {
    formData.append('eventId', form.eventId);
  }

  if (form.taskNumberId) {
    formData.append('taskNumberId', form.taskNumberId);
  }

  if (form.kanbanNumber) {
    formData.append('kanbanNumber', form.kanbanNumber);
  }

  if (form.kanbanName) {
    formData.append('kanbanName', form.kanbanName);
  }

  if (form.scriptedById) {
    formData.append('scriptedById', form.scriptedById);
  }

  if (form.scriptedDateTime) {
    formData.append('scriptedDateTime', form.scriptedDateTime.toString());
  }

  if (form.modifiedById) {
    formData.append('modifiedById', form.modifiedById);
  }

  if (form.modifiedDateTime) {
    formData.append('modifiedDateTime', form.modifiedDateTime.toString());
  }
  if (form.emir.length > 0) {
    formData.append('emir', form.emir.join(','));
  }
  if (form.kanbanCoordinatorId) {
    formData.append('kanbanCoordinatorId', form.kanbanCoordinatorId);
  }

  if (form.actors) {
    form.actors.forEach((e) => formData.append('actors', e as any as string));

    // formData.append('actors', actorsConvert);
  }

  if (form.dateTimeGroup) {
    formData.append('dateTimeGroup', form.dateTimeGroup.toString());
  }
  //  else {
  //   formData.append('dateTimeGroup', '0');
  // }

  if (form.timeDetails) {
    formData.append('timeDetails', JSON.stringify(form.timeDetails));
  }

  if (form.type) {
    formData.append('type', form.type.toString());
  }

  if (form.projectInputReferenceId) {
    formData.append('projectInputReferenceId', form.projectInputReferenceId);
  }
  if (form.kanbanReferenceId) {
    formData.append('kanbanReferenceId', form.kanbanReferenceId);
  }

  if (form.duration) {
    formData.append('duration', form.duration);
  }

  if (form.description) {
    formData.append('description', form.description);
  } else {
    formData.append('description', '');
  }

  if (form.coordinationRemarks) {
    formData.append('coordinationRemarks', form.coordinationRemarks);
  } else {
    formData.append('coordinationRemarks', '');
  }

  // if (form.unitInSimulation) {
  //   formData.append('unitInSimulation', form.unitInSimulation);
  // }
  if (form.units && form.units.length > 0) {
    formData.append('units', form.units.join(','));
  }

  if (form.log) {
    formData.append('log', form.log);
  }

  if (form.comment) {
    formData.append('comment', form.comment);
  } else {
    formData.append('comment', '');
  }

  if (form.kanbanStatusId) {
    formData.append('kanbanStatusId', form.kanbanStatusId);
  }
  formData.append('isAnchor', form.isAnchor.toString());

  return axios.patch(urlsConfig.kanban.kanbanEdit, formData, { blockErrorHandler: true });
};

export const kanbanDelete = ({
  id,
}: serviceKanbanTypes.KanbanDeleteDataType): AxiosPromise<serviceKanbanTypes.KanbanDeleteResultType> => {
  return axios.delete(`${urlsConfig.kanban.kanbanDelete}/${id}`);
};
